import type { ChangeEvent, FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GenericAutocomplete } from "../../../../common/GenericAutocomplete";
import type {
  CompareObject,
  ComparisonObjectValue,
} from "../../../../services/api/instances/comparisonAPI/types";
import type { ChatCompletionsMessageObject } from "../../../../services/api/instances/completionsApi/types";
import type { Model } from "../../../../services/api/instances/modelsApi/types";
import { useAppDispatch } from "../../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import { AppIconButton } from "../../../../shared/components/AppIconButton";
import { AppUnderlinedTitle } from "../../../../shared/components/AppUnderlinedTitle";
import { ClearIcon } from "../../../../shared/icons/ClearIcon";
import { TrashIcon } from "../../../../shared/icons/TrashIcon";
import { getModel, replaceItemById } from "../../helpers";
import { useCompare } from "../../hooks/useCompare";
import { updateComparisonObjectAction } from "../../redux/actions";
import {
  selectComparisonModels,
  selectComparisonObjects,
  selectComparisonValue,
  selectSelectedComparisonObject,
} from "../../redux/selectors";
import {
  setComparisonObjects,
  setComparisonObjectValue,
  setSelectedComparisonObject,
} from "../../redux/slice";
import { ObjectNameChange } from "../ObjectNameChange";
import styles from "./styles.module.css";
import { DisplayedMessages } from "../../../../common/ChatComponents/DisplayedMessages";
import { StorageObject } from "../../../../services/api/instances/storageApi/types/object_types";

interface Props {
  id: string;
  messages: ChatCompletionsMessageObject[];
  isLoading: boolean;
}

export const ComparisonChat: FC<Props> = ({ id, messages, isLoading }) => {
  const dispatch = useAppDispatch();
  const models = useAppSelector(selectComparisonModels);
  const objectValue = useAppSelector(selectComparisonValue);
  const comparisonObjects = useAppSelector(selectComparisonObjects);
  const selectedComparisonObject = useAppSelector(
    selectSelectedComparisonObject,
  );
  const { updateComparisonObject, updateObjectsWithNewValue } = useCompare();
  const [model, setModel] = useState<Model | null>(null);
  const [objectName, setObjectName] = useState<string>(
    selectedComparisonObject?.friendly_name!,
  );
  const [modelName, setModelName] = useState<string>("");
  const { t } = useTranslation();

  useEffect(() => {
    if (selectedComparisonObject && selectedComparisonObject.value !== null) {
      const newModel = getModel(models?.models as Model[], objectValue, id);
      setModel(newModel as Model);
      setModelName(newModel?.friendly_name as string);
      const checkValue = objectValue?.find((item) => item.system_role_name);
      if (!checkValue) {
        const newValue = objectValue?.map((item) => {
          if (item.system_role_name) {
            return item;
          } else {
            return {
              ...item,
              system_role_name: newModel?.system_role_name,
            };
          }
        });
        const newComparisonObject = updateObjectsWithNewValue(
          selectedComparisonObject,
          newValue,
        );
        dispatch(updateComparisonObjectAction(newComparisonObject));
      }
    }
  }, [selectedComparisonObject?.id, models]);

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setObjectName(event.target.value);
  };

  const handleChangeModel = (valueId: string, newValue: Model) => {
    const selectedModel = models?.models.find(
      (item) => item?.friendly_name === newValue?.friendly_name,
    );
    setModel(selectedModel as Model);
    const comparisonObjectValue = selectedComparisonObject?.value.llm_compare;
    const newObjectValue = comparisonObjectValue?.map((object) => {
      if (object.id === valueId) {
        return {
          ...object,
          model: selectedModel?.model_name,
          system_role_name: selectedModel?.system_role_name,
        };
      }
      return object;
    });
    const newComparisonObject = {
      ...selectedComparisonObject,
      value: {
        llm_compare: newObjectValue,
      },
    };
    const updatedComparisonObjects = replaceItemById(
      comparisonObjects,
      newComparisonObject as StorageObject<ComparisonObjectValue>,
    );
    dispatch(
      updateComparisonObjectAction(
        newComparisonObject as StorageObject<ComparisonObjectValue>,
      ),
    );
    dispatch(setComparisonObjectValue(newObjectValue as CompareObject[]));
    dispatch(
      setSelectedComparisonObject(
        newComparisonObject as StorageObject<ComparisonObjectValue>,
      ),
    );
    dispatch(setComparisonObjects(updatedComparisonObjects));
  };

  const handleClearChat = (chatId: string) => {
    const newValue = objectValue?.map((item) => {
      if (item.id === chatId) {
        return {
          ...item,
          messages: item.messages.filter(
            (message) => message.role === "system",
          ),
        };
      }
      return item;
    });
    const newComparisonObject = {
      ...selectedComparisonObject,
      value: {
        llm_compare: newValue,
      },
    };
    const updatedComparisonObjects = replaceItemById(
      comparisonObjects,
      newComparisonObject as StorageObject<ComparisonObjectValue>,
    );
    dispatch(
      updateComparisonObjectAction(
        newComparisonObject as StorageObject<ComparisonObjectValue>,
      ),
    );
    dispatch(setComparisonObjectValue(newValue as CompareObject[]));
    dispatch(
      setSelectedComparisonObject(
        newComparisonObject as StorageObject<ComparisonObjectValue>,
      ),
    );
    dispatch(setComparisonObjects(updatedComparisonObjects));
  };

  const handleDeleteChat = (chatId: string) => {
    const newValue = objectValue?.filter((item) => item.id !== chatId);
    const newComparisonObject = {
      ...selectedComparisonObject,
      value: {
        llm_compare: newValue,
      },
    };
    const updatedComparisonObjects = replaceItemById(
      comparisonObjects,
      newComparisonObject as StorageObject<ComparisonObjectValue>,
    );
    dispatch(
      updateComparisonObjectAction(
        newComparisonObject as StorageObject<ComparisonObjectValue>,
      ),
    );
    dispatch(setComparisonObjectValue(newValue as CompareObject[]));
    dispatch(
      setSelectedComparisonObject(
        newComparisonObject as StorageObject<ComparisonObjectValue>,
      ),
    );
    dispatch(setComparisonObjects(updatedComparisonObjects));
  };

  const handleEditName = () => {
    const postObj = {
      ...selectedComparisonObject,
      friendly_name: objectName,
    };
    const updatedComparisonObjects = replaceItemById(
      comparisonObjects,
      postObj as StorageObject<ComparisonObjectValue>,
    );
    updateComparisonObject(postObj as StorageObject<ComparisonObjectValue>);
    dispatch(
      setSelectedComparisonObject(
        postObj as StorageObject<ComparisonObjectValue>,
      ),
    );
    dispatch(setComparisonObjects(updatedComparisonObjects));
  };

  return (
    <div className={styles.chat}>
      <AppUnderlinedTitle
        leftContent={
          <GenericAutocomplete<Model>
            options={models?.models as Model[]}
            value={model}
            onChange={(newValue) => handleChangeModel(id, newValue as Model)}
            getValue={(option) => option.model_name}
            getLabel={(option: Model) => option?.friendly_name}
            groupBy={(option: Model) => option.server_type}
            getOptionKey={(option) => option.model_name}
            autocompleteStyle={"custom"}
          />
        }
      >
        <div className={styles["button-wrapper"]}>
          <ObjectNameChange
            name={objectName}
            onEditName={handleEditName}
            handleNameChange={handleNameChange}
            onCancel={() => setObjectName("")}
          />
          <AppIconButton onClick={() => handleClearChat(id)}>
            <ClearIcon />
          </AppIconButton>
          <AppIconButton onClick={() => handleDeleteChat(id)}>
            <TrashIcon />
          </AppIconButton>
        </div>
      </AppUnderlinedTitle>
      <DisplayedMessages
        messages={messages}
        isLoading={isLoading}
        className={styles["comparison-massage-display"]}
      />
    </div>
  );
};
